/**
 * THE service-worker registration — the one place it happens, behind the one
 * gate that decides whether this shell runs a worker at all.
 *
 * Called by composables/useAppUpdate.startUpdateWatch, which owns the update
 * lifecycle (job #759). It used to be vite-plugin-pwa's `useRegisterSW` inside
 * PwaUpdatePrompt; the app now registers itself so that it, not the plugin,
 * decides when the waiting worker activates and when the page reloads.
 *
 * Registered after the FIRST SOUND, not at `load` (job #298). On a first visit
 * registering installs the worker, and the install downloads the whole boot
 * shell (~1.5 MB, 300+ files). From `load` that ran beside the first clip: on
 * the #287 preview the worker had fetched 460-630 KB, 16-24 files in flight
 * across the first clip's download, before the learner heard a word. Nothing
 * about WHAT is cached moves, only when: a page with no player still
 * registers, at the first-sound signal's own no-player fallback, and
 * REGISTER_CEILING_MS bounds a player that never sounds.
 */
import { shouldRunServiceWorker } from './capabilities'
import { firstSound } from '../playback/firstSoundSignal'

/** Past this from the call, register whether or not anything has sounded. */
export const REGISTER_CEILING_MS = 20_000

/** After the first sound, leave the next clips this long before the install starts. */
export const AFTER_FIRST_SOUND_MS = 1_500

export function registerServiceWorker(deps: { afterFirstSound?: () => Promise<void> } = {}): boolean {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false
  if (!shouldRunServiceWorker()) return false
  let done = false
  const register = () => {
    if (done) return
    done = true
    clearTimeout(ceiling)
    window.removeEventListener('load', register)
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then(() => console.log('[PWA] Service worker registered'))
      .catch((err) => console.error('[PWA] Service worker registration error:', err))
  }
  const ceiling = setTimeout(register, REGISTER_CEILING_MS)
  const afterFirstSound = deps.afterFirstSound ?? firstSound
  void afterFirstSound().then(() => {
    setTimeout(() => {
      // Keep the ceiling armed until registration, even if page load stalls.
      if (document.readyState === 'complete') register()
      else window.addEventListener('load', register, { once: true })
    }, AFTER_FIRST_SOUND_MS)
  })
  return true
}
